package er.prateek.crossly.data.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * One candidate answer and the clue(s) that can front it.
 *
 * Field names are single letters on the wire because this repeats ~11k times;
 * the [SerialName] mapping keeps the Kotlin side readable.
 */
@Serializable
data class WordEntry(
    @SerialName("w") val word: String,
    @SerialName("c") val clues: List<String>,
    @SerialName("t") val tier: DifficultyTier,
    /** Frequency rank in English usage, 0 = most common. Lower is easier. */
    @SerialName("r") val rank: Int,
) {
    val length: Int get() = word.length
}

/**
 * The bundled offline lexicon a puzzle generator draws from, bucketed by word
 * length because that's the only index a grid fill ever needs: every slot in a
 * template has a known length, and candidates are looked up by it.
 *
 * Built by `tools/wordbank/build_wordbank.py` from WordNet 3.0 crossed with an
 * English frequency list -- see `tools/wordbank/README.md`. Entries inside each
 * bucket are ordered most-common-first, so a generator targeting an easy tier
 * can take a prefix rather than filtering the whole bucket.
 */
@Serializable
data class WordBank(
    val schemaVersion: Int,
    val language: String = "en",
    val source: String = "",
    @SerialName("entriesByLength") private val rawEntriesByLength: Map<String, List<WordEntry>>,
) {
    /** Word length -> candidates of that length, most common first. */
    val entriesByLength: Map<Int, List<WordEntry>> by lazy {
        rawEntriesByLength.mapKeys { (length, _) -> length.toInt() }
    }

    val allEntries: List<WordEntry> get() = entriesByLength.values.flatten()

    fun wordsOfLength(length: Int): List<WordEntry> = entriesByLength[length].orEmpty()

    /**
     * Candidates of [length] at or below [tier]'s difficulty, most common
     * first. Easier tiers stay in the pool deliberately: restricting a HARD
     * puzzle to only obscure words makes it unfair, not harder.
     */
    fun wordsFor(length: Int, tier: DifficultyTier): List<WordEntry> =
        wordsOfLength(length).filter { it.tier.ordinal <= tier.ordinal }

    /**
     * Like [wordsFor], but widens into harder tiers until at least [minSize]
     * candidates are available.
     *
     * Variety across a tier is bounded by how many words the fill can draw
     * from: with 30 levels of a 3x3 grid needing 180 answers, a strict EASY
     * pool of ~190 three-letter words forces heavy reuse no matter how clever
     * the search is. Short words are also where tier labels mean least -- a
     * rank-10000 three-letter word is still easy to get from crossings -- so
     * widening the shallow buckets costs little difficulty-wise.
     */
    fun fillPoolFor(
        length: Int,
        tier: DifficultyTier,
        minSize: Int,
        maxTiersWider: Int = 2,
    ): List<WordEntry> {
        val ceiling = minOf(tier.ordinal + maxTiersWider, DifficultyTier.entries.lastIndex)
        var candidateTier = tier
        while (true) {
            val pool = wordsFor(length, candidateTier)
            val next = DifficultyTier.entries.getOrNull(candidateTier.ordinal + 1)
            if (pool.size >= minSize || next == null || next.ordinal > ceiling) return pool
            candidateTier = next
        }
    }
}
